import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaColumns, FaPencilAlt } from "react-icons/fa";
import { format } from "date-fns";
import axios from "axios";
import ModifyColumns from "./../Tools/ModifyColumns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
  renderTableCell,
  getCurrencySymbol,
} from "../config/tableHelper";
import { RenderTable } from "../config/tableWrapper";
import {
  CAMPAIGN_TYPE_COLUMNS,
  GROUP_CAMPAIGN_TYPE_COLUMNS,
} from "../Components/AddTableCols";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CampaignTable = ({ email, customerId, initialDateRange }) => {
  const [groups, setGroups] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(
    initialDateRange[0].startDate.toLocaleString("default", { month: "long" }),
  );
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [isEditing, setIsEditing] = useState({});
  const [tempBudgets, setTempBudgets] = useState({});
  const filterRef = useRef(null);
  const cardColumnsRef = useRef(null);

  const [defaultcolumns, setDefaultColumns] = useState(() => {
    return CAMPAIGN_TYPE_COLUMNS.map((col) => {
      if (col.id === "2" || col.key === `Total Budget for "this month"`) {
        return {
          ...col,
          title: `Budget for ${selectedMonth}`,
        };
      }
      return col;
    });
  });

  const [columns, setColumns] = useState(defaultcolumns);
  const [currentGroupType, setCurrencyGroupType] = useState("");

  const toTitleCase = (str = "") =>
    str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const fetchGroups = useCallback(async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      const resInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const groupType = await resInfo.json();
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      setCurrencyGroupType(
        typeForQuery?.charAt(0).toUpperCase() + typeForQuery?.slice(1),
      );
      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error(err);
    }
  }, [initialDateRange]);

  const fetchData = async () => {
    try {
      const startDate = format(initialDateRange[0].startDate, "yyyy-MM-dd");
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      setIsLoading(true);
      const res = await fetch(
        "https://api.confidanto.com/campaigns/campaign-types-overview",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const rawData = await res.json();
      if (Array.isArray(rawData)) {
        setCampaignData(
          rawData.map((item) => {
            const campaignName = Object.keys(item)[0];
            const metrics = item[campaignName];
            return {
              campaign_name: campaignName,
              ...metrics,
              this_month_budget: metrics.total_budget,
              [`Total Budget for "this month"`]: metrics.total_budget,
            };
          }),
        );
      }
      const dynamicMonth = new Date(startDate).toLocaleString("default", {
        month: "long",
      });
      setSelectedMonth(dynamicMonth);

      // Re-initialize columns back to CAMPAIGN_TYPE_COLUMNS when returning to default view
      setColumns(
        CAMPAIGN_TYPE_COLUMNS.map((col) =>
          col.key === `Total Budget for "this month"` || col.id === "2"
            ? { ...col, title: `Budget for ${dynamicMonth}` }
            : col,
        ),
      );
    } catch (err) {
      setCampaignData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setAppliedFilters([]);
    fetchGroups();
    fetchData();
  }, [initialDateRange, customerId, email]);

  const handleSaveClick = async (id) => {
    const newBudget = Number(tempBudgets[id]);
    if (isNaN(newBudget) || newBudget < 0) return;
    const ReqBody = apiRequestBodyBuilder(null, {
      projectId: Number(localStorage.getItem("project_id")) || 259,
      campaignType: id,
      customer_id: localStorage.getItem("customer_id"),
      month: format(initialDateRange[0].startDate, "yyyy-MM"),
      total_budget: newBudget,
      email: localStorage.getItem("email"),
    });
    try {
      await axios.post("https://api.confidanto.com/monthly-budget", ReqBody);
      setCampaignData((prev) =>
        prev.map((c) =>
          c.campaign_name === id
            ? {
                ...c,
                this_month_budget: newBudget,
                remaining_budget: newBudget - (c.costs || 0),
              }
            : c,
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsEditing((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleGroupApply = async (remainingGroups = null) => {
    setIsLoading(true);
    const activeGroups =
      remainingGroups !== null ? remainingGroups : selectedGroups;
    const selectedGroupIds = groups
      .filter((g) => activeGroups.includes(g.group_name))
      .map((g) => g.group_id);
    if (activeGroups.length === 0) {
      alert("Please select at least one group.");
      setIsLoading(false);
      return;
    }
    setAppliedFilters(
      activeGroups.map((g) => ({
        id: g,
        type: "groupwise",
        value: g,
        label: g,
      })),
    );
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        group_ids: selectedGroupIds,
      });
      const res = await axios.post(
        "https://api.confidanto.com/campaign-type-group/datewise-data",
        requestBody,
      );
      setCampaignData(res.data);
      setColumns(
        GROUP_CAMPAIGN_TYPE_COLUMNS.map((col) =>
          col.key === "group_name"
            ? {
                ...col,
                title: `${(res?.data[0]?.type || "campaign").toUpperCase()} GROUP`,
              }
            : col,
        ),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFilter = (filterToRemove) => {
    const newFilters = appliedFilters.filter((f) => f.id !== filterToRemove.id);
    if (filterToRemove.type === "groupwise") {
      const remainingGroups = newFilters
        .filter((f) => f.type === "groupwise")
        .map((f) => f.value);
      setSelectedGroups(remainingGroups);
      if (remainingGroups.length === 0) {
        setAppliedFilters(newFilters);
        fetchData();
      } else {
        handleGroupApply(remainingGroups);
      }
    } else {
      setAppliedFilters(newFilters);
    }
  };

  const getFilteredData = () => {
    const staticFilters = appliedFilters.filter((f) => f.type === "static");
    if (staticFilters.length === 0) return campaignData;
    return campaignData.filter((row) =>
      staticFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key]?.value
            : row[filter.key];
        if (rowValue === null || rowValue === undefined) return false;
        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue),
            filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          if (filter.comparison === ">") return rowNum > filterNum;
          if (filter.comparison === "<") return rowNum < filterNum;
          if (filter.comparison === "=") return rowNum === filterNum;
          return true;
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      }),
    );
  };

  const renderCellContent = (col, rowData) => {
    const id = rowData.campaign_name;
    if (col.key === "campaign_name")
      return toTitleCase(id ? id.replace(/_/g, " ") : "");
    if (col.key === 'Total Budget for "this month"') {
      return isEditing[id] ? (
        <div className="flex items-center justify-end">
          <input
            type="number"
            value={tempBudgets[id] || ""}
            onChange={(e) =>
              setTempBudgets((p) => ({ ...p, [id]: e.target.value }))
            }
            className="border p-1 w-24 text-right"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleSaveClick(id)}
          />
          <button
            className="bg-blue-500 text-white p-1 rounded ml-1 text-xs"
            onClick={() => handleSaveClick(id)}
          >
            Save
          </button>
        </div>
      ) : (
        <div className="flex justify-end items-center gap-2">
          <span>
            {getCurrencySymbol() +
              (rowData.this_month_budget ?? 0).toLocaleString()}
          </span>
        </div>
      );
    }
    return renderTableCell(col, rowData);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardColumnsRef.current && !cardColumnsRef.current.contains(e.target))
        setShowColumnsMenu(false);
      if (
        filterRef.current &&
        !filterRef.current.contains(e.target) &&
        !e.target.closest(".groupwise-select-menu")
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="bg-gray-100 font-roboto p-4 text-sm">
      <div className="flex justify-between items-center mb-3">
        <AddTableFilterComponent
          data={campaignData}
          filterRef={filterRef}
          showFilterDropdown={showFilterDropdown}
          setShowFilterDropdown={setShowFilterDropdown}
          appliedFilters={appliedFilters}
          handleRemoveFilter={handleRemoveFilter}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          columns={columns}
          groups={groups}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          onApplyGroupFilter={handleGroupApply}
          onApplyStaticFilter={(f) => {
            setAppliedFilters((p) => [...p, f]);
            setShowFilterDropdown(false);
          }}
          currentGroupType={currentGroupType}
        />
        <div className="relative flex space-x-2" ref={cardColumnsRef}>
          <button
            className="bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center hover:bg-slate-100"
            onClick={() => setShowColumnsMenu(!showColumnsMenu)}
          >
            <FaColumns className="text-sm " />
            <span className="text-sm">Columns</span>
          </button>
          {showColumnsMenu && (
            <div className="absolute -top-2 right-0">
              <ModifyColumns
                columns={columns}
                setColumns={setColumns}
                setShowColumnsMenu={setShowColumnsMenu}
                colsLimit={13}
              />
            </div>
          )}
        </div>
      </div>

      {/* Table Section with Skeleton Loader */}
      {isLoading ? (
        <div className="mt-3">
          <TableSkeleton />
        </div>
      ) : !getFilteredData() || getFilteredData().length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-lg">
          No campaign data available.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded shadow mt-3">
          <RenderTable
            columns={columns}
            data={getFilteredData()}
            renderCellContent={renderCellContent}
            enableExpandCollapse={initialDateRange.length > 1}
          />
        </div>
      )}
    </div>
  );
};

export default CampaignTable;
